import { ColorType, IChartApi } from 'lightweight-charts'
import { isEmbeddedModeSet } from '../../util/general'
import { callNative } from '../../util/nativeBridge'
import { getPaneElement } from '../../util/chartPanes'

/**
 * "Powered by tick2trade" mark on charts, for partner / white-label builds (VITE_CHART_BRAND_MARK=true). It
 * replaces the chart library's own attribution logo; builds without the flag keep that logo and show no mark.
 */
export const isChartBrandMarkEnabled = (): boolean => import.meta.env.VITE_CHART_BRAND_MARK === 'true'

const MARK_ATTR = 'data-t2t-brand'
const STYLE_ID = 't2t-chart-brand-style'
const MAX_PLACE_ATTEMPTS = 8
const TICK2TRADE_URL = 'https://tick2trade.com'
const BRAND_LABEL = 'Powered by tick2trade'

const LOGO_SVG = `<svg viewBox="0 0 20.712 20.712" aria-hidden="true"><path d="M5.178 7.767V20.712H20.712V16.829H9.062V0H5.178V3.884H0V7.767H5.178Z" fill="currentColor"/><path d="M11.651 0V14.24H20.712V10.356H15.534V7.767H20.712V3.884H15.534V0H11.651Z" fill="currentColor"/></svg>`

const BRAND_ON_DARK = '#D1D4DC'
const BRAND_ON_LIGHT = '#000000'

const ensureStyle = () => {
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
    if (!style) {
        style = document.createElement('style')
        style.id = STYLE_ID
        document.head.appendChild(style)
    }
    style.textContent = `
[${MARK_ATTR}] {
    position: absolute;
    left: 10px;
    bottom: 10px;
    z-index: 4;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    padding: 0;
    pointer-events: none;
    user-select: none;
    line-height: 1;
    white-space: nowrap;
    font-family: 'Work Sans', -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.02em;
}
[${MARK_ATTR}] span {
    font-size: 1em;
    line-height: 1;
    -webkit-text-stroke: 4px var(--t2t-brand-halo, #fff);
    paint-order: stroke fill;
}
[${MARK_ATTR}] svg {
    width: 1cap;
    height: 1cap;
    display: block;
    flex: 0 0 auto;
}
[${MARK_ATTR}] svg path {
    stroke: var(--t2t-brand-halo, #fff);
    stroke-width: 2.5px;
    stroke-linejoin: round;
    stroke-linecap: round;
    paint-order: stroke fill;
    vector-effect: non-scaling-stroke;
}
[${MARK_ATTR}="button"] {
    pointer-events: auto;
    cursor: pointer;
    gap: 0;
    height: 32px;
    width: 32px;
    padding: 0 8px;
    overflow: hidden;
    justify-content: flex-start;
    border: 0;
    border-radius: 16px;
    background: transparent;
    color: #D1D4DC;
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0;
    appearance: none;
    -webkit-appearance: none;
    box-sizing: border-box;
    touch-action: manipulation;
    transition: width 320ms cubic-bezier(0.22, 1, 0.36, 1), gap 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
[${MARK_ATTR}="button"]:not([data-collapsed]) {
    width: var(--t2t-brand-open, 32px);
    gap: 6px;
}
[${MARK_ATTR}="button"] span,
[${MARK_ATTR}="button"] svg path {
    -webkit-text-stroke: 0;
    stroke: none;
    paint-order: normal;
}
[${MARK_ATTR}="button"] > span {
    display: block;
    min-width: 0;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    transform: translateX(-8px);
    transition: max-width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 160ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
[${MARK_ATTR}="button"]:not([data-collapsed]) > span {
    max-width: 220px;
    opacity: 1;
    transform: translateX(0);
    transition: max-width 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease 70ms, transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
[${MARK_ATTR}="button"] > span > span {
    display: block;
    white-space: nowrap;
}
[${MARK_ATTR}="button"] svg {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
}
@media (prefers-reduced-motion: reduce) {
    [${MARK_ATTR}="button"],
    [${MARK_ATTR}="button"] > span {
        transition: none;
    }
}
`
}

const backgroundColor = (chart: IChartApi): string => {
    const color = chart.options().layout.background
    if (color.type === ColorType.VerticalGradient) return color.bottomColor
    return color.color
}

const luminance = (color: string): number => {
    const probe = document.createElement('canvas').getContext('2d')
    if (!probe) return 0
    probe.fillStyle = '#000'
    probe.fillStyle = color
    const normalized = probe.fillStyle
    let r = 0
    let g = 0
    let b = 0
    if (normalized.startsWith('#')) {
        const hex = normalized.slice(1)
        const full = hex.length === 3 ? hex.split('').map((channel) => channel + channel).join('') : hex
        r = parseInt(full.slice(0, 2), 16)
        g = parseInt(full.slice(2, 4), 16)
        b = parseInt(full.slice(4, 6), 16)
    } else {
        const match = normalized.match(/\d+/g)
        if (!match) return 0
        ;[r, g, b] = match.map(Number)
    }
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 0
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const brandColorFor = (chart: IChartApi): string => (luminance(backgroundColor(chart)) > 160 ? BRAND_ON_LIGHT : BRAND_ON_DARK)

const openTick2TradeWebsite = () => {
    const hasNativeHandler = Boolean(
        window.Android?.onExternalLinkClicked || window.webkit?.messageHandlers?.onExternalLinkClicked
    )
    if (isEmbeddedModeSet() && hasNativeHandler) {
        callNative('onExternalLinkClicked', TICK2TRADE_URL)
        return
    }
    window.open(TICK2TRADE_URL, '_blank', 'noopener,noreferrer')
}

const brandButtons = new Set<HTMLButtonElement>()
let outsideListenerBound = false

const onOutsideBrandButton = (event: Event) => {
    brandButtons.forEach((button) => {
        if (!button.isConnected) {
            brandButtons.delete(button)
            return
        }
        if (event.target instanceof Node && button.contains(event.target)) return
        button.setAttribute('data-collapsed', '')
        button.setAttribute('aria-label', BRAND_LABEL)
    })
    if (brandButtons.size === 0 && outsideListenerBound) {
        document.removeEventListener('pointerdown', onOutsideBrandButton, true)
        outsideListenerBound = false
    }
}

const bindBrandButton = (button: HTMLButtonElement) => {
    if (button.dataset.bound === '1') return
    button.dataset.bound = '1'
    button.setAttribute('aria-label', BRAND_LABEL)

    const stopChartGesture = (event: Event) => {
        event.stopPropagation()
    }
    button.addEventListener('pointerdown', stopChartGesture)
    button.addEventListener('mousedown', stopChartGesture)
    button.addEventListener('touchstart', stopChartGesture, { passive: true })
    button.addEventListener('click', (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (button.hasAttribute('data-collapsed')) {
            button.removeAttribute('data-collapsed')
            button.removeAttribute('aria-label')
            return
        }
        button.setAttribute('data-collapsed', '')
        button.setAttribute('aria-label', BRAND_LABEL)
        openTick2TradeWebsite()
    })

    brandButtons.add(button)
    if (!outsideListenerBound) {
        document.addEventListener('pointerdown', onOutsideBrandButton, true)
        outsideListenerBound = true
    }
}

const placeBrandMark = (root: HTMLElement, chart: IChartApi, embedded: boolean) => {
    const mode = embedded ? 'button' : 'static'
    let mark = root.querySelector<HTMLElement>(`[${MARK_ATTR}]`)
    if (!mark || mark.getAttribute(MARK_ATTR) !== mode) {
        mark?.remove()
        if (embedded) {
            const button = document.createElement('button')
            button.type = 'button'
            button.setAttribute(MARK_ATTR, 'button')
            button.setAttribute('data-collapsed', '')
            button.innerHTML = `${LOGO_SVG}<span><span>${BRAND_LABEL}</span></span>`
            bindBrandButton(button)
            mark = button
        } else {
            mark = document.createElement('div')
            mark.setAttribute(MARK_ATTR, 'static')
            mark.innerHTML = `${LOGO_SVG}<span>tick2trade</span>`
        }
        root.appendChild(mark)
    }
    if (embedded) {
        const paper = backgroundColor(chart)
        mark.style.backgroundColor = paper
        mark.style.color = brandColorFor(chart)
        const text = mark.querySelector('span span') as HTMLElement | null
        if (text) mark.style.setProperty('--t2t-brand-open', `${32 + 6 + text.scrollWidth}px`)
    } else {
        mark.style.color = brandColorFor(chart)
        mark.style.setProperty('--t2t-brand-halo', backgroundColor(chart))
    }
}

export const syncChartBrandMark = (chart: IChartApi, paneIndex: number | (() => number)): boolean => {
    const root = chart.chartElement()
    if (!isChartBrandMarkEnabled()) {
        root?.querySelector(`[${MARK_ATTR}]`)?.remove()
        return true
    }
    ensureStyle()
    const index = typeof paneIndex === 'function' ? paneIndex() : paneIndex
    const container = getPaneElement(chart.panes()[index])
    if (!root || !container) return false
    root.querySelectorAll(`[${MARK_ATTR}]`).forEach((node) => {
        if (node.parentElement !== container) node.remove()
    })
    placeBrandMark(container, chart, isEmbeddedModeSet())
    return true
}

export const scheduleChartBrandMark = (chart: IChartApi, paneIndex: number | (() => number)): (() => void) => {
    let attempts = 0
    let frame = 0
    let cancelled = false
    const place = () => {
        if (cancelled) return
        try {
            if (syncChartBrandMark(chart, paneIndex) || attempts >= MAX_PLACE_ATTEMPTS) return
        } catch {
            return
        }
        attempts += 1
        frame = requestAnimationFrame(place)
    }
    place()
    return () => {
        cancelled = true
        cancelAnimationFrame(frame)
    }
}
